import { X } from "lucide-react";
import type { RatingWithEffectiveDebits } from "../../types";
import { Card } from "../Card";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { TagPill } from "../Pills";
import { formatCurrency, truncate } from "../../utils/format";
import { SHORT_NOTE_PREVIEW_CHARACTERS } from "../member/NoteEditor";
import type { RatingSelectionDraft } from "./ratingSelection";

export interface SelectedRatingEntry {
  rating: RatingWithEffectiveDebits;
  draft: RatingSelectionDraft;
}

interface SelectedRatingsPanelProps {
  entries: readonly SelectedRatingEntry[];
  isAdding: boolean;
  onRemove: (ratingId: number) => void;
  onAdd: () => void;
  children?: never;
}

/** Sticky "cart" of checked ratings so a long rating list never hides the selections. */
export function SelectedRatingsPanel({ entries, isAdding, onRemove, onAdd }: SelectedRatingsPanelProps) {
  return (
    <div className="xl:sticky xl:top-24">
      <Card title="Selected ratings" actions={<span className="text-xs text-gray-500">{entries.length} checked</span>}>
        {entries.length === 0 && <p className="text-sm text-gray-600">Check ratings on the left. They collect here with any override, UW Dollars, or IRIX flag you set.</p>}
        {entries.length > 0 && (
          <ul className="mb-4 divide-y divide-gray-100">
            {entries.map(({ rating, draft }) => {
              const debits = draft.debitsOverride ?? rating.genericSubstitute?.effectiveDebits ?? rating.effectiveDebits;
              return (
                <li key={rating.ratingId} className="flex items-start justify-between gap-2 py-1.5 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800">{rating.ratingText}</p>
                    <p className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
                      <span className="tabular-nums">{debits} debits</span>
                      {draft.debitsOverride !== null && <TagPill label="override" tone="green" />}
                      {draft.uwDollars !== null && <span>{formatCurrency(draft.uwDollars)}</span>}
                      {draft.irixSource && <TagPill label="IRIX" tone="navy" />}
                      {draft.notes.trim() !== "" && <span title={draft.notes}>“{truncate(draft.notes, SHORT_NOTE_PREVIEW_CHARACTERS)}”</span>}
                    </p>
                  </div>
                  <IconButton icon={X} label={`Remove ${rating.ratingText} from selection`} onClick={() => onRemove(rating.ratingId)} className="!min-h-[32px] !min-w-[32px]" />
                </li>
              );
            })}
          </ul>
        )}
        <Button onClick={onAdd} disabled={entries.length === 0} isLoading={isAdding} className="w-full">
          Add to Member ({entries.length})
        </Button>
      </Card>
    </div>
  );
}
